import React, { useState, useEffect, useCallback } from "react";
import Modal from "./Modal";
import "./Analytics.css";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

function formatDateLocal(date) {
  // Returns YYYY-MM-DD in local time
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const today = new Date();
  const defaultEnd = formatDateLocal(today); // local today
  const defaultStart = formatDateLocal(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)); // last 7 days

  const [periodEnd, setPeriodEnd] = useState(defaultEnd);
  const [periodStart, setPeriodStart] = useState(defaultStart);

  const [modalData, setModalData] = useState(null);
  const [modalTitle, setModalTitle] = useState("");

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `https://admin.deedelicious.com/php/analytics.php?start=${periodStart}&end=${periodEnd}`
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Failed to load analytics");
      setAnalytics(data.analytics);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading)
    return (
      <div className="spinner-overlay">
        today
        <div className="spinner"></div>
      </div>
    );
  if (error) return <p className="analytics-error">Error: {error}</p>;
  if (!analytics) return <p className="analytics-empty">No data available</p>;

  const {
    ordersSummary = [],
    topItems = [],
    topCustomers = [],
    ordersByType = [],
    totalOrders = 0,
    totalRevenue = 0,
  } = analytics;

  // Best Seller
  const bestSeller = topItems[0] || null;

  // Top Customers
  const topCustomerValue = topCustomers[0] || null;
  const topCustomerVolume = topCustomers.reduce(
    (max, c) => (c.total_orders > (max?.total_orders || 0) ? c : max),
    null
  );

  // Sort topCustomers for volume table
  const topCustomersByVolume = [...topCustomers].sort(
    (a, b) => b.total_orders - a.total_orders
  );

  return (
    <div className="analytics-page">
      <h1 className="analytics-heading">Analytics Dashboard</h1>

      {/* Date Picker */}
      <div className="analytics-date-picker">
        <label>
          Start Date:{" "}
          <input
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </label>
        <label>
          End Date:{" "}
          <input
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </label>
        <button className="analytics-apply-btn" onClick={fetchAnalytics}>
          Apply
        </button>
      </div>

      {/* Cards */}
      {/* Cards */}
      <div className="analytics-cards">
        <div className="analytics-card">
          <h3>Total Orders</h3>
          <p>{totalOrders}</p>
        </div>
        <div className="analytics-card">
          <h3>Total Revenue</h3>
          <p>£{totalRevenue.toFixed(2)}</p>
        </div>
        <div className="analytics-card">
          <h3>Best Seller</h3>
          <p>
            {totalOrders > 0 && bestSeller?.quantity_sold > 0
              ? bestSeller.item_name
              : "N/A"}
          </p>
        </div>
        <div className="analytics-card">
          <h3>Top Customer (Value)</h3>
          <p>
            {totalOrders > 0 && topCustomerValue?.total_value > 0
              ? `${topCustomerValue.first_name} ${topCustomerValue.last_name}`
              : "N/A"}
          </p>
        </div>
        <div className="analytics-card">
          <h3>Top Customer (Volume)</h3>
          <p>
            {totalOrders > 0 && topCustomerVolume?.total_orders > 0
              ? `${topCustomerVolume.first_name} ${topCustomerVolume.last_name}`
              : "N/A"}
          </p>
        </div>
      </div>

      {/* Tables */}
      <div className="analytics-tables-container">
        {/* Orders Summary */}
        <div className="analytics-table-section">
          <h2>Orders Summary</h2>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Total Orders</th>
                <th>Total Revenue (£)</th>
              </tr>
            </thead>
            <tbody>
              {ordersSummary.length > 0 ? (
                ordersSummary.slice(0, 5).map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.period}</td>
                    <td>{row.total_orders}</td>
                    <td>{row.total_revenue.toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    style={{ textAlign: "center", fontStyle: "italic" }}
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {ordersSummary.length > 5 && (
            <button
              onClick={() => {
                setModalData(ordersSummary);
                setModalTitle("Orders Summary");
              }}
            >
              View All
            </button>
          )}
        </div>

        {/* Top Selling Items */}
        <div className="analytics-table-section">
          <h2>Top Selling Items</h2>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Quantity Sold</th>
                <th>Total Revenue (£)</th>
              </tr>
            </thead>
            <tbody>
              {topItems.slice(0, 5).map((item) => (
                <tr key={item.id}>
                  <td>{item.item_name}</td>
                  <td>{item.quantity_sold}</td>
                  <td>{item.total_revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {topItems.length > 5 && (
            <button
              onClick={() => {
                setModalData(topItems);
                setModalTitle("All Top Selling Items");
              }}
            >
              View All
            </button>
          )}
        </div>

        {/* Top Customers (Value) */}
        <div className="analytics-table-section">
          <h2>Top Customer (Value)</h2>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Total Value (£)</th>
              </tr>
            </thead>
            <tbody>
              {topCustomers.slice(0, 5).map((cust) => (
                <tr key={cust.id}>
                  <td>
                    {cust.first_name} {cust.last_name}
                  </td>
                  <td>{cust.full_phone}</td>
                  <td>{cust.total_value.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {topCustomers.length > 5 && (
            <button
              onClick={() => {
                setModalData(topCustomers);
                setModalTitle("Top Customers (Value)");
              }}
            >
              View All
            </button>
          )}
        </div>

        {/* Top Customers (Volume) */}
        <div className="analytics-table-section">
          <h2>Top Customer (Volume)</h2>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Total Orders</th>
              </tr>
            </thead>
            <tbody>
              {topCustomersByVolume.slice(0, 5).map((cust) => (
                <tr key={cust.id}>
                  <td>
                    {cust.first_name} {cust.last_name}
                  </td>
                  <td>{cust.full_phone}</td>
                  <td>{cust.total_orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {topCustomersByVolume.length > 5 && (
            <button
              onClick={() => {
                setModalData(topCustomersByVolume);
                setModalTitle("Top Customers (Volume)");
              }}
            >
              View All
            </button>
          )}
        </div>
      </div>

      {/* Charts */}
      <div className="analytics-charts">
        {ordersByType.length > 0 && (
          <div className="chart-container">
            <h3>Orders by Type</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  dataKey="count"
                  data={ordersByType}
                  nameKey="type"
                  outerRadius={80}
                  label={(entry) => {
                    const total = ordersByType.reduce(
                      (sum, e) => sum + e.count,
                      0
                    );
                    const percent = ((entry.count / total) * 100).toFixed(1);
                    return `${entry.type}: ${entry.count} (${percent}%)`;
                  }}
                >
                  {ordersByType.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => {
                    const total = ordersByType.reduce(
                      (sum, e) => sum + e.count,
                      0
                    );
                    const percent = ((value / total) * 100).toFixed(1);
                    return [`${value} (${percent}%)`, name];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalData && (
        <Modal
          title={modalTitle}
          onClose={() => setModalData(null)}
          showCancel={false}
          showSubmitButton={false}
        >
          <table className="analytics-table">
            <thead>
              <tr>
                {modalData[0] &&
                  Object.keys(modalData[0])
                    .filter((key) => key !== "id") // hide id
                    .map((key) => (
                      <th key={key}>
                        {/* Friendly column names */}
                        {key
                          .replace(/_/g, " ") // underscores → spaces
                          .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
                        {/* capitalize */}
                      </th>
                    ))}
              </tr>
            </thead>
            <tbody>
              {modalData.map((row, idx) => (
                <tr key={idx}>
                  {Object.entries(row)
                    .filter(([key]) => key !== "id") // hide id
                    .map(([key, val]) => (
                      <td key={key}>
                        {typeof val === "number" && key.includes("revenue")
                          ? val.toFixed(2)
                          : val}
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
};

export default Analytics;
